/**
 * Production-Ready - TaxableValueTable (Builder Pattern)
 * Based on engine's pretty-print output structure
 * Backend integration pending (see docs/planning/bond-calculator-app-plan.md)
 */

import { Badge, Box, Heading, HStack, Text, Tooltip } from '@chakra-ui/react';
import type React from 'react';
import { useMemo } from 'react';

import { type ColumnDefinition, ReusableTable } from '~/components/tables/ReusableTable';
import { TableWithHeader } from '~/components/tables/TableWithHeader';
import type { TaxableValueMap, TaxableValueMapEntry } from '~/types';
import type { TaxableValueOverrides } from '~/types/overrides';

// Support both data structures for flexibility
type TaxableValueRecord = {
  taxYear: number;
  growthRate: number;
  taxableValue: number;
  yearOverYearChange?: number;
};

// Extended entry type that includes override information
interface TaxableValueTableEntry extends TaxableValueMapEntry {
  isOverridden?: boolean;
  originalGrowthRate?: number;
}

interface TaxableValueTableProps {
  data?: TaxableValueMapEntry[];
  taxableValueMap?: TaxableValueMap | null;
  title?: string;
  isLoading?: boolean;
  error?: string | null;
  showSummary?: boolean;
  /** Optional overrides to highlight in the table */
  overrides?: TaxableValueOverrides | null;
}

// Utility function to format currency (no decimal places for large values)
const formatCurrency = (value: number): string => {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(value);
};

// Utility function to format percentage (using Intl.NumberFormat percent style)
const formatPercentage = (value: number): string => {
  return new Intl.NumberFormat('en-US', {
    style: 'percent',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(value);
};

// Convert TaxableValueMap to array format
const convertToTableData = (taxableValueMap: TaxableValueMap): TaxableValueMapEntry[] => {
  return Object.values(taxableValueMap)
    .sort((a, b) => a.taxYear - b.taxYear)
    .map((item) => ({
      taxYear: item.taxYear,
      growthRate: item.growthRate,
      taxableValue: item.taxableValue,
    }));
};

// Calculate summary statistics
const calculateSummary = (data: TaxableValueMapEntry[]) => {
  if (data.length === 0) return null;

  const firstYear = data[0];
  const lastYear = data[data.length - 1];
  const totalGrowth = lastYear.taxableValue - firstYear.taxableValue;
  const totalGrowthPercentage = (totalGrowth / firstYear.taxableValue) * 100;

  return {
    startingValue: firstYear.taxableValue,
    endingValue: lastYear.taxableValue,
    totalGrowth,
    totalGrowthPercentage,
    yearSpan: data.length,
    averageAnnualGrowth: totalGrowth / data.length,
  };
};

export const TaxableValueTable: React.FC<TaxableValueTableProps> = ({
  data,
  taxableValueMap,
  title = 'Taxable Value Projections',
  isLoading = false,
  error = null,
  showSummary = false,
  overrides,
}) => {
  // Determine actual data to use, enriched with override information
  const tableData: TaxableValueTableEntry[] = useMemo(() => {
    const baseData = data || (taxableValueMap ? convertToTableData(taxableValueMap) : []);

    // If no overrides, return base data
    if (!overrides || Object.keys(overrides.byYear).length === 0) {
      return baseData;
    }

    // Enrich data with override information
    return baseData.map((entry) => {
      const override = overrides.byYear[entry.taxYear];
      if (override) {
        return {
          ...entry,
          isOverridden: true,
          originalGrowthRate: override.originalGrowthRate,
        };
      }
      return entry;
    });
  }, [data, taxableValueMap, overrides]);

  // Count of overridden values for header badge
  const overrideCount = useMemo(() => {
    return tableData.filter((entry) => entry.isOverridden).length;
  }, [tableData]);

  // Define column structure with override-aware rendering
  const columns: ColumnDefinition<TaxableValueTableEntry>[] = [
    {
      key: 'taxYear',
      header: 'Tax Year',
      accessor: 'taxYear',
      headerTextAlign: 'center',
      cellTextAlign: 'center',
    },
    {
      key: 'taxableValue',
      header: 'Taxable Value',
      accessor: (item) => formatCurrency(item.taxableValue),
      headerTextAlign: 'right',
      cellTextAlign: 'right',
    },
    {
      key: 'growthRate',
      header: 'Growth Rate',
      accessor: (item) => {
        const formattedRate = formatPercentage(item.growthRate);

        if (item.isOverridden && item.originalGrowthRate !== undefined) {
          // Show overridden growth rate with tooltip showing original
          return (
            <Tooltip.Root>
              <Tooltip.Trigger asChild>
                <HStack gap={1} justify="flex-end">
                  <Text color="yellow.700" fontWeight="semibold">
                    {formattedRate}
                  </Text>
                  <Badge size="sm" colorPalette="yellow" variant="subtle">
                    *
                  </Badge>
                </HStack>
              </Tooltip.Trigger>
              <Tooltip.Positioner>
                <Tooltip.Content>
                  <Text fontSize="xs">Original: {formatPercentage(item.originalGrowthRate)}</Text>
                </Tooltip.Content>
              </Tooltip.Positioner>
            </Tooltip.Root>
          );
        }

        return formattedRate;
      },
      headerTextAlign: 'right',
      cellTextAlign: 'right',
    },
  ];

  // Handle loading state
  if (isLoading) {
    return (
      <Box>
        <Heading size="md" mb={4}>
          {title}
        </Heading>
        <Text color="gray.500">Loading taxable value projections...</Text>
      </Box>
    );
  }

  // Handle error state
  if (error) {
    return (
      <Box>
        <Heading size="md" mb={4}>
          {title}
        </Heading>
        <Text color="red.500">Error: {error}</Text>
      </Box>
    );
  }

  // Handle empty data
  if (!tableData || tableData.length === 0) {
    return (
      <Box>
        <Heading size="md" mb={4}>
          {title}
        </Heading>
        <Text color="gray.500">No taxable value data available</Text>
      </Box>
    );
  }

  // Calculate summary if requested
  const summary = showSummary ? calculateSummary(tableData) : null;

  return (
    <Box height="100%" display="flex" flexDirection="column" minHeight={0}>
      {/* Summary section */}
      {summary && (
        <Box mb={4} p={4} flexShrink={0}>
          <Text fontSize="sm" fontWeight="bold" mb={2}>
            Summary
          </Text>
          <Text fontSize="sm">
            <strong>Starting Value:</strong> {formatCurrency(summary.startingValue)}
          </Text>
          <Text fontSize="sm">
            <strong>Ending Value:</strong> {formatCurrency(summary.endingValue)}
          </Text>
          <Text fontSize="sm">
            <strong>Total Growth:</strong> {formatCurrency(summary.totalGrowth)} (
            {summary.totalGrowthPercentage.toFixed(1)}%)
          </Text>
          <Text fontSize="sm">
            <strong>Average Annual Growth:</strong> {formatCurrency(summary.averageAnnualGrowth)}
          </Text>
        </Box>
      )}

      {/* Override count badge (if applicable) */}
      {overrideCount > 0 && (
        <HStack mb={2}>
          <Badge colorPalette="yellow" variant="subtle" size="sm">
            {overrideCount} value{overrideCount > 1 ? 's' : ''} customized
          </Badge>
        </HStack>
      )}

      {/* Data table */}
      <Box flex={1} minHeight={0} display="flex" flexDirection="column">
        <TableWithHeader title={title} totalRows={tableData.length}>
          <ReusableTable columns={columns} data={tableData} />
        </TableWithHeader>
      </Box>
    </Box>
  );
};

// Export types for use in other files
export type { TaxableValueMap, TaxableValueRecord };
